import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";
import { V3IconButton } from "@/components/ui/v3/button";
import { cn } from "@/lib/utils";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  // The form around body and footer, so Enter submits.
  onSubmit?: () => void;
  footer: React.ReactNode;
  children: React.ReactNode;
  width?: "default" | "wide";
  testId?: string;
}

// The referral screens' v3.2 sheet (as V3ConfirmSheet and V3Drawer): a
// bottom sheet with a grabber on phones and a centred card from 640px.
// Title row with the close button, a scrolling body and a pinned footer.
export const ReferralSheet = ({
  open,
  onOpenChange,
  title,
  description,
  onSubmit,
  footer,
  children,
  width = "default",
  testId,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/30 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0" />
        <Dialog.Content
          aria-describedby={undefined}
          className={cn(
            "fixed z-50 flex max-h-[92dvh] flex-col bg-v3-panel font-v3 text-v3-text shadow-v3-pop outline-hidden",
            "max-sm:inset-x-0 max-sm:bottom-0 max-sm:rounded-t-[22px] max-sm:data-[state=open]:animate-in max-sm:data-[state=open]:slide-in-from-bottom-10 max-sm:data-[state=closed]:animate-out max-sm:data-[state=closed]:slide-out-to-bottom-10",
            "sm:left-1/2 sm:top-1/2 sm:max-h-[min(88dvh,820px)] sm:w-[calc(100vw-32px)] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-[18px] sm:data-[state=open]:animate-in sm:data-[state=open]:zoom-in-95 sm:data-[state=open]:fade-in-0",
            width === "wide" ? "sm:max-w-[640px]" : "sm:max-w-[560px]",
          )}
          data-testid={testId}
          tabIndex={-1}
          // Start in the first field, not on the close button.
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            const content = event.currentTarget as HTMLElement | null;
            const field = content?.querySelector<HTMLElement>(
              "form input:not([type=file]):not(.sr-only), form textarea",
            );
            (field ?? content)?.focus({ preventScroll: true });
          }}
        >
          <div
            aria-hidden
            className="mx-auto mt-2 h-1 w-9 shrink-0 rounded-full bg-v3-border-strong sm:hidden"
          />
          <div className="flex shrink-0 items-start gap-2 pb-3 pl-5 pr-2 pt-3 sm:pl-6 sm:pt-5">
            <div className="min-w-0 flex-1 space-y-1 pt-1.5">
              <Dialog.Title className="font-v3-display text-[18px] font-semibold leading-6 tracking-[-0.01em]">
                {title}
              </Dialog.Title>
              {description && (
                <Dialog.Description className="text-[13.5px] leading-5 text-v3-text-2">
                  {description}
                </Dialog.Description>
              )}
            </div>
            <Dialog.Close asChild>
              <V3IconButton label={t("common.close")}>
                <X />
              </V3IconButton>
            </Dialog.Close>
          </div>
          <form
            className="flex min-h-0 flex-1 flex-col"
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              onSubmit?.();
            }}
          >
            <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 pb-5 sm:px-6">
              {children}
            </div>
            <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-v3-border px-5 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:flex-row sm:justify-end sm:px-6">
              {footer}
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
